import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import { Loader2, Save, SlidersHorizontal, RefreshCw, DollarSign, ShieldCheck, AlertTriangle, Check, ChevronsUpDown } from 'lucide-react';

// Maps EQUIPR hour types → the accounting platform's payroll category / earnings rate.
const RATE_ROWS = [
  { key: 'ordinary', label: 'Ordinary hours', required: true },
  { key: 'overtime_1_5', label: 'Overtime (1.5x)', required: false },
  { key: 'overtime_2_0', label: 'Overtime (2.0x)', required: false },
  { key: 'travel', label: 'Travel', required: false },
];

const NONE = '__none__';

/** MYOB Job searchable combobox for rate mapping */
function MyobJobCombobox({ value, jobs, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = jobs.find(j => j.uid === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 justify-between w-full font-normal text-sm"
          data-testid={testId}
        >
          <span className="truncate">
            {selected ? `${selected.number} – ${selected.name}` : 'Select MYOB job...'}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search jobs..." />
          <CommandList>
            <CommandEmpty className="text-sm p-2 text-center text-muted-foreground">No jobs found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__clear__"
                onSelect={() => { onChange(''); setOpen(false); }}
                className="text-sm text-muted-foreground"
              >
                <span>No default job</span>
                {!value && <Check className="ml-auto h-4 w-4" />}
              </CommandItem>
              {jobs.map(j => (
                <CommandItem
                  key={j.uid}
                  value={`${j.number} ${j.name}`}
                  onSelect={() => { onChange(j.uid); setOpen(false); }}
                >
                  <span className="font-mono mr-1">{j.number}</span> – {j.name}
                  {value === j.uid && <Check className="ml-auto h-4 w-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function PayrollRateMapping({ api, platform, platformName, connectionId }) {
  const [categories, setCategories] = useState([]);
  const [mapping, setMapping] = useState({ ordinary: '', overtime_1_5: '', overtime_2_0: '', travel: '' });
  const [allowanceTypes, setAllowanceTypes] = useState([]);
  const [allowanceMappings, setAllowanceMappings] = useState({});
  const [trackingCategories, setTrackingCategories] = useState([]);   // Flat options (legacy)
  const [rawTrackingCategories, setRawTrackingCategories] = useState([]); // Full categories with options
  const [timesheetCategoryId, setTimesheetCategoryId] = useState(null);
  const [trackingItemId, setTrackingItemId] = useState('');
  const [trackingDefaults, setTrackingDefaults] = useState([]);       // [{category_id, option_id}]
  const [myobDefaultJobUid, setMyobDefaultJobUid] = useState('');
  const [myobJobs, setMyobJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const connQ = connectionId ? `?connection_id=${connectionId}` : '';
      const catEndpoint = platform === 'myob'
        ? `/payroll/myob/payroll-categories${connQ}`
        : `/payroll/xero/earnings-rates${connQ}`;
      const mapEndpoint = connectionId
        ? `/payroll/rate-mapping?platform=${platform}&connection_id=${connectionId}`
        : '/payroll/rate-mapping';
      const promises = [
        api.get(catEndpoint),
        api.get(mapEndpoint),
        api.get('/allowance-types'),
      ];
      // Fetch Xero tracking categories (only for Xero)
      if (platform === 'xero') {
        promises.push(api.get(`/payroll/xero/tracking-categories${connQ}`).catch((err) => {
          console.warn('Failed to fetch Xero tracking categories:', err?.response?.status, err?.response?.data?.detail || err.message);
          return { data: { tracking_categories: [], timesheet_category_id: null } };
        }));
      }
      // Fetch MYOB jobs (only for MYOB)
      if (platform === 'myob') {
        promises.push(api.get(`/payroll/myob/jobs${connQ}`).catch((err) => {
          console.warn('Failed to fetch MYOB jobs:', err?.response?.status, err?.response?.data?.detail || err.message);
          return { data: { jobs: [] } };
        }));
      }
      const [catRes, mapRes, alRes, trackOrJobRes] = await Promise.all(promises);
      const cats = platform === 'myob'
        ? (catRes.data.payroll_categories || [])
        : (catRes.data.earnings_rates || []);
      setCategories(cats);
      setAllowanceTypes((alRes.data || []).filter(t => t.is_active));

      // Flatten tracking category options for the dropdown (legacy)
      if (platform === 'xero' && trackOrJobRes) {
        const rawCats = trackOrJobRes.data?.tracking_categories || [];
        setRawTrackingCategories(rawCats);
        setTimesheetCategoryId(trackOrJobRes.data?.timesheet_category_id || null);
        const allOptions = [];
        for (const cat of rawCats) {
          for (const opt of (cat.options || [])) {
            if (opt.status === 'ACTIVE') {
              allOptions.push({ id: opt.id, name: `${cat.name}: ${opt.name}`, category_id: cat.id });
            }
          }
        }
        setTrackingCategories(allOptions);
      }
      // Load MYOB jobs
      if (platform === 'myob' && trackOrJobRes) {
        setMyobJobs(trackOrJobRes.data?.jobs || []);
      }

      const existing = connectionId ? (mapRes.data || {}) : ((mapRes.data && mapRes.data[platform]) || {});
      setMapping({
        ordinary: existing.ordinary || '',
        overtime_1_5: existing.overtime_1_5 || '',
        overtime_2_0: existing.overtime_2_0 || '',
        travel: existing.travel || '',
      });
      setAllowanceMappings(existing.allowance_mappings || {});
      setTrackingItemId(existing.xero_tracking_item_id || '');
      setTrackingDefaults(existing.xero_tracking_defaults || []);
      setMyobDefaultJobUid(existing.myob_default_job_uid || '');
    } catch (err) {
      setError(err.response?.data?.detail || `Could not load ${platformName} payroll categories`);
    } finally {
      setLoading(false);
    }
  }, [api, platform, platformName, connectionId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!mapping.ordinary) {
      toast.error('Ordinary hours must be mapped before saving');
      return;
    }
    setSaving(true);
    try {
      await api.post('/payroll/rate-mapping', {
        platform,
        connection_id: connectionId || null,
        ordinary: mapping.ordinary || null,
        overtime_1_5: mapping.overtime_1_5 || null,
        overtime_2_0: mapping.overtime_2_0 || null,
        travel: mapping.travel || null,
        allowance_mappings: allowanceMappings,
        xero_tracking_item_id: trackingItemId || null,
        xero_tracking_defaults: trackingDefaults.length > 0 ? trackingDefaults : null,
        myob_default_job_uid: myobDefaultJobUid || null,
      });
      toast.success(`${platformName} rate mapping saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rate mapping');
    } finally {
      setSaving(false);
    }
  };

  const setRow = (key, value) => {
    setMapping((prev) => ({ ...prev, [key]: value === NONE ? '' : value }));
  };

  const setAllowanceRow = (typeId, value) => {
    setAllowanceMappings((prev) => {
      const updated = { ...prev };
      if (value === NONE || !value) {
        delete updated[typeId];
      } else {
        updated[typeId] = value;
      }
      return updated;
    });
  };

  const handleValidate = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      // Refresh live rates from Xero
      const connQ = connectionId ? `?connection_id=${connectionId}` : '';
      const catEndpoint = platform === 'myob'
        ? `/payroll/myob/payroll-categories${connQ}`
        : `/payroll/xero/earnings-rates${connQ}`;
      const catRes = await api.get(catEndpoint);
      const liveCats = platform === 'myob'
        ? (catRes.data.payroll_categories || [])
        : (catRes.data.earnings_rates || []);
      setCategories(liveCats);

      const liveIds = new Set(liveCats.map(c => c.id));
      const results = [];

      // Check hour type mappings
      for (const row of RATE_ROWS) {
        const mappedId = mapping[row.key];
        if (!mappedId) continue;
        const match = liveCats.find(c => c.id === mappedId);
        results.push({
          label: row.label,
          mappedId,
          mappedName: match?.name || null,
          valid: liveIds.has(mappedId),
        });
      }

      // Check allowance mappings
      for (const [typeId, rateId] of Object.entries(allowanceMappings)) {
        if (!rateId) continue;
        const at = allowanceTypes.find(a => a.id === typeId);
        const match = liveCats.find(c => c.id === rateId);
        results.push({
          label: at?.name ? `Allowance: ${at.name}` : `Allowance: ${typeId.slice(0, 8)}…`,
          mappedId: rateId,
          mappedName: match?.name || null,
          valid: liveIds.has(rateId),
        });
      }

      const allValid = results.length === 0 || results.every(r => r.valid);
      setValidationResult({ results, allValid });
      if (allValid && results.length > 0) {
        toast.success('All mapped earnings rates are valid');
      } else if (!allValid) {
        toast.error('Some mapped earnings rates are invalid — see details below');
      } else {
        toast.info('No rates are mapped yet');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to validate ${platformName} rates`);
    } finally {
      setValidating(false);
    }
  };

  return (
    <Card className="card-industrial" data-testid={`payroll-rate-mapping-${platform}`}>
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
          {platformName} Rate Mapping
        </CardTitle>
        <CardDescription>
          Map each EQUIPR hour type and allowance to a {platformName} {platform === 'myob' ? 'payroll category' : 'earnings rate'} so timesheets export with the correct pay categories.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading categories...
          </div>
        ) : error ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-red-600">{error}</p>
            <Button variant="outline" size="sm" onClick={load} data-testid={`rate-mapping-retry-${platform}`}>
              <RefreshCw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.length === 0 && (
              <p className="text-sm text-amber-600">
                No {platform === 'myob' ? 'payroll categories' : 'earnings rates'} found in {platformName}. Create them in {platformName} first, then Retry.
              </p>
            )}

            {/* Hour type mappings */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Hours</Label>
              {RATE_ROWS.map((row) => (
                <div key={row.key} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                  <Label className="text-sm">
                    {row.label}
                    {row.required && <span className="text-red-500"> *</span>}
                  </Label>
                  <Select value={mapping[row.key] || NONE} onValueChange={(v) => setRow(row.key, v)}>
                    <SelectTrigger data-testid={`rate-map-${platform}-${row.key}`}>
                      <SelectValue placeholder="Not mapped" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not mapped</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Allowance type mappings */}
            {allowanceTypes.length > 0 && (
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Allowances
                </Label>
                {allowanceTypes.map((at) => (
                  <div key={at.id} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                    <Label className="text-sm flex items-center gap-2">
                      {at.name}
                      <Badge variant="outline" className="text-[10px] font-normal">{at.unit === 'per_hour' ? '/hr' : at.unit === 'per_day' ? '/day' : 'fixed'}</Badge>
                    </Label>
                    <Select
                      value={allowanceMappings[at.id] || NONE}
                      onValueChange={(v) => setAllowanceRow(at.id, v)}
                    >
                      <SelectTrigger data-testid={`rate-map-${platform}-allowance-${at.id}`}>
                        <SelectValue placeholder="Not mapped" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not mapped</SelectItem>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}

            {/* Xero Tracking Category */}
            {platform === 'xero' && rawTrackingCategories.length > 0 && (
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Xero Tracking Categories</Label>
                {timesheetCategoryId ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Xero uses a single timesheet tracking category. Select the default tracking option below.
                    </p>
                    {(() => {
                      const tsCat = rawTrackingCategories.find(c => c.id === timesheetCategoryId);
                      if (!tsCat) return <p className="text-xs text-red-400">Timesheet category not found in categories.</p>;
                      const currentDefault = trackingDefaults.find(td => td.category_id === tsCat.id);
                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                          <Label className="text-sm">
                            Default {tsCat.name}
                          </Label>
                          <Select
                            value={currentDefault?.option_id || NONE}
                            onValueChange={(v) => {
                              const filtered = trackingDefaults.filter(td => td.category_id !== tsCat.id);
                              if (v !== NONE) {
                                filtered.push({ category_id: tsCat.id, option_id: v });
                              }
                              setTrackingDefaults(filtered);
                              if (filtered.length > 0) setTrackingItemId(filtered[0].option_id);
                            }}
                          >
                            <SelectTrigger data-testid={`rate-map-xero-tracking-${tsCat.id}`}>
                              <SelectValue placeholder="Not mapped" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>Not mapped</SelectItem>
                              {(tsCat.options || []).filter(o => o.status === 'ACTIVE').map((opt) => (
                                <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Your Xero organisation has {rawTrackingCategories.length} tracking categor{rawTrackingCategories.length === 1 ? 'y' : 'ies'}. Select a default option for each.
                    </p>
                    {rawTrackingCategories.slice(0, 2).map((cat) => {
                      const currentDefault = trackingDefaults.find(td => td.category_id === cat.id);
                      return (
                        <div key={cat.id} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                          <Label className="text-sm">
                            {cat.name} <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={currentDefault?.option_id || NONE}
                            onValueChange={(v) => {
                              const filtered = trackingDefaults.filter(td => td.category_id !== cat.id);
                              if (v !== NONE) filtered.push({ category_id: cat.id, option_id: v });
                              setTrackingDefaults(filtered);
                              if (filtered.length > 0) setTrackingItemId(filtered[0].option_id);
                            }}
                          >
                            <SelectTrigger data-testid={`rate-map-xero-tracking-${cat.id}`}>
                              <SelectValue placeholder="Not mapped" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>Not mapped</SelectItem>
                              {(cat.options || []).filter(o => o.status === 'ACTIVE').map((opt) => (
                                <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* Legacy single tracking dropdown — fallback if no raw cats */}
                {rawTrackingCategories.length === 0 && trackingCategories.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                    <Label className="text-sm">
                      Tracking Item <span className="text-red-500">*</span>
                    </Label>
                    <Select value={trackingItemId || NONE} onValueChange={(v) => setTrackingItemId(v === NONE ? '' : v)}>
                      <SelectTrigger data-testid="rate-map-xero-tracking-item">
                        <SelectValue placeholder="Not mapped" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not mapped</SelectItem>
                        {trackingCategories.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* MYOB Default Job — searchable combobox */}
            {platform === 'myob' && (
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">MYOB Job Tracking</Label>
                <p className="text-xs text-muted-foreground">
                  Optionally assign a default MYOB Job reference to timesheet lines. Per-block overrides can be set on individual time blocks.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                  <Label className="text-sm">Default Job</Label>
                  {myobJobs.length > 0 ? (
                    <MyobJobCombobox
                      value={myobDefaultJobUid}
                      jobs={myobJobs}
                      onChange={setMyobDefaultJobUid}
                      testId="rate-map-myob-default-job"
                    />
                  ) : (
                    <input
                      type="text"
                      value={myobDefaultJobUid}
                      onChange={(e) => setMyobDefaultJobUid(e.target.value)}
                      placeholder="MYOB Job UID (optional)"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="rate-map-myob-default-job"
                    />
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 pt-2 border-t">
              <div className="flex justify-between items-center">
                <Button variant="outline" onClick={handleValidate} disabled={validating} data-testid={`validate-rate-mapping-${platform}`}>
                  {validating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating...</> : <><ShieldCheck className="h-4 w-4 mr-2" /> Validate Earnings Rates</>}
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid={`save-rate-mapping-${platform}`}>
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-2" /> Save Mapping</>}
                </Button>
              </div>

              {validationResult && (
                <div className={`rounded-lg border p-3 space-y-2 ${validationResult.allValid ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5'}`} data-testid={`rate-validation-results-${platform}`}>
                  <p className={`text-xs font-medium flex items-center gap-1.5 ${validationResult.allValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {validationResult.allValid ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                    {validationResult.results.length === 0
                      ? 'No rates are mapped yet.'
                      : validationResult.allValid
                        ? `All ${validationResult.results.length} mapped rate(s) are valid in ${platformName}.`
                        : `Some rates no longer exist in ${platformName}. Re-map them to valid rates and save.`}
                  </p>
                  {validationResult.results.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      {r.valid
                        ? <Check className="h-3 w-3 text-green-500 shrink-0" />
                        : <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />}
                      <span className={r.valid ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400 font-medium'}>{r.label}</span>
                      <span className="text-muted-foreground/70">→</span>
                      <span className={r.valid ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'}>
                        {r.valid ? r.mappedName : `ID not found: ${r.mappedId.slice(0, 8)}…`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
